import React from 'react';
import { Link } from '../../atoms/Link';
import { Text } from '../../atoms/Text';
import { ListItem } from '../../atoms/ListItem';

export interface MenuItemProps {
  /**
   * Link URL
   */
  href: string;
  /**
   * Menu item text
   */
  children: React.ReactNode;
  /**
   * Is this a sub-item (indented text)
   */
  isSubItem?: boolean;
  /**
   * Click event handler
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * External link (opens in new tab)
   */
  external?: boolean;
  /**
   * Target attribute
   */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /**
   * HTML title attribute
   */
  title?: string;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Active/current state (highlights this item)
   */
  active?: boolean;
  /**
   * Sub-items to render below this item
   */
  subItems?: React.ReactNode;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  href,
  children,
  isSubItem = false,
  onClick,
  external = false,
  target,
  title,
  className = '',
  active = false,
  subItems,
}) => {
  const baseClass = 'hi-ui hi-ui-menu-item';
  const subItemClass = isSubItem ? 'hi-ui-menu-item-sub' : '';
  const activeClass = active ? 'hi-ui-menu-item-active' : '';

  const allClasses = `${baseClass} ${subItemClass} ${activeClass} ${className}`.trim();

  // Handle external links
  const linkTarget = external ? '_blank' : target;
  const linkRel = external ? 'noopener noreferrer' : undefined;

  return (
    <>
      <ListItem className={allClasses}>
        <Link
          href={href}
          onClick={onClick}
          target={linkTarget}
          rel={linkRel}
          title={title}
          className="hi-ui-menu-item-link"
          external={external}
          noUnderline
        >
          <Text variant="inline" className="hi-ui-menu-item-text">
            {children}
          </Text>
        </Link>
      </ListItem>
      {subItems}
    </>
  );
};
